import AccountConfigurationProvider, {
  AccountConfigurationContext,
} from "@shared/features/configuration/AccountConfiguration/AccountConfigurationProvider";
import HedgeModeSelector from "features/configuration/accounts/AccountConfigurationDrawer/AccountConfiguration/HedgeModeSelector";
import React, { useContext } from "react";
import Styles from "features/configuration/common/styles";
import Button from "common/Buttons/Button";
import { useAccountConfigurationForm } from "@shared/features/configuration/AccountConfiguration/hooks";
import { displayResultMessage, openMessage } from "utils/misc";
import EmptyView from "common/Empty/EmptyView";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid";
import useMedia from "utils/hooks/useMedia.ts";
import { MOBILE_SEARCH } from "common/constants.ts";
import { EmptyStatesType } from "common/Icons/types.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

interface AccountConfigurationProps {
  accountId?: number;
  isPreview?: boolean;
}

const AccountConfiguration = ({
  accountId,
  isPreview,
}: AccountConfigurationProps) => {
  return (
    <AccountConfigurationProvider accountId={accountId} isPreview={isPreview}>
      <AccountConfigurationForm />
    </AccountConfigurationProvider>
  );
};

export default AccountConfiguration;

const AccountConfigurationForm = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { isPreview } = useContext(AccountConfigurationContext)!;
  const { saveButtonData, isEmptyConfiguration } = useAccountConfigurationForm({
    openMessage,
    displayResultMessage,
  });

  const getFormContent = () => {
    if (isEmptyConfiguration) {
      return (
        <EmptyView
          iconKey={EmptyStatesType.Configuration}
          iconWidth={isMobile ? 110 : 140}
          iconStyle={{ marginBottom: 8 }}
          description={t("account_configuration.empty_message")}
          descriptionStyle={{ maxWidth: 250 }}
        />
      );
    } else if (isPreview) {
      return (
        <EqualColumnsGrid numColumns={2}>
          <HedgeModeSelector />
        </EqualColumnsGrid>
      );
    }
    return <HedgeModeSelector />;
  };

  if (!isPreview) {
    return (
      <Styles.CardsWrapper style={{ maxWidth: "100%" }}>
        <Styles.CardsTitle
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          {t("account_configuration.title")}
          {saveButtonData.shouldDisplay && (
            <Button
              loading={saveButtonData.isLoading}
              disabled={saveButtonData.disabled}
              onClick={saveButtonData.onClick}
            >
              {saveButtonData.text}
            </Button>
          )}
        </Styles.CardsTitle>
        {getFormContent()}
      </Styles.CardsWrapper>
    );
  }

  return getFormContent();
};
